"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OpenPanelState = void 0;
const rxjs_1 = require("rxjs");
const COOL_DOWN_TIME = 69;
class OpenPanelState {
    constructor(opts = {}) {
        this.opts = opts;
        this.canSelectAfter = 0;
        this.lastClosed = '';
        this.hovered = '';
        this.focusStack = [];
        this.pendingTimer = 0;
        this.armed = false;
        this.start = () => {
            if (this.armed) {
                return () => {
                    this.clearPending();
                };
            }
            // Gate hover until the first mousemove so the menu does not auto-open a
            // submenu wherever the cursor happens to be when it pops up.
            const onMove = () => {
                this.armed = true;
                document.removeEventListener('mousemove', onMove, true);
            };
            document.addEventListener('mousemove', onMove, true);
            return () => {
                document.removeEventListener('mousemove', onMove, true);
                this.clearPending();
            };
        };
        this.hover = (id) => {
            if (!this.armed)
                return;
            const raw = this.selected$.value;
            const selected = this.toLocal(raw);
            if (id === this.lastClosed && selected !== id)
                return;
            if (!raw) {
                this.clearPending();
                this.forceSelect(id);
                return;
            }
            const now = Date.now();
            if (selected === id) {
                this.clearPending();
                this.canSelectAfter = now + COOL_DOWN_TIME;
                this.hovered = id;
                return;
            }
            if (now <= this.canSelectAfter) {
                this.canSelectAfter = now + COOL_DOWN_TIME;
                this.hovered = id;
                this.schedulePending(id);
                return;
            }
            this.clearPending();
            this.hovered = id;
            this.select(id);
        };
        this.select = (id) => {
            const raw = this.selected$.value;
            if (id === '') {
                if (raw && this.isOurs(raw))
                    this.deselect();
                return;
            }
            if (this.isOurs(raw) && raw === this.prefix + id) {
                this.deselect();
                return;
            }
            const now = Date.now();
            if (now > this.canSelectAfter) {
                this.forceSelect(id, now + COOL_DOWN_TIME);
            }
        };
        this.onClick = this.select;
        this.onMouseMove = this.hover;
        this.onMouseLeave = () => {
            this.clearPending();
            this.hovered = '';
            this.lastClosed = '';
        };
        const { selected$, prefix = '', armed = false } = opts;
        this.selected$ = selected$ ?? new rxjs_1.BehaviorSubject('');
        this.prefix = prefix;
        this.armed = armed;
    }
    isSelected(localId) {
        if (!localId)
            return false;
        return this.selected$.value === this.prefix + localId;
    }
    isOurs(raw) {
        if (!raw)
            return false;
        return !this.prefix || raw.startsWith(this.prefix);
    }
    toLocal(raw) {
        return this.isOurs(raw) ? raw.slice(this.prefix.length) : '';
    }
    clearPending() {
        if (this.pendingTimer) {
            clearTimeout(this.pendingTimer);
            this.pendingTimer = 0;
        }
    }
    schedulePending(id) {
        this.clearPending();
        const delay = this.canSelectAfter - Date.now();
        if (delay <= 0)
            return;
        this.pendingTimer = setTimeout(() => {
            this.pendingTimer = 0;
            if (this.hovered === id && !this.isSelected(id)) {
                this.forceSelect(id);
            }
        }, delay);
    }
    deselect() {
        const raw = this.selected$.value;
        if (!raw || !this.isOurs(raw))
            return false;
        this.clearPending();
        this.lastClosed = raw.slice(this.prefix.length);
        this.hovered = '';
        this.canSelectAfter = Date.now() + COOL_DOWN_TIME;
        this.selected$.next('');
        // Restore focus to the parent item that opened this submenu.
        const prev = this.focusStack.pop();
        if (prev && typeof prev.focus === 'function')
            requestAnimationFrame(() => prev.focus());
        return true;
    }
    /**
     * Forcefully select an item, ignoring any pending unlock cool down.
     *
     * @param id Local id of the item to select (the prefix is added internally).
     */
    forceSelect(id, canSelectAfter = Date.now() + COOL_DOWN_TIME) {
        this.hovered = id;
        this.canSelectAfter = canSelectAfter;
        // Save the currently focused element so we can restore it on deselect.
        const focused = document.activeElement;
        if (focused instanceof HTMLElement)
            this.focusStack.push(focused);
        this.selected$.next(this.prefix + id);
    }
}
exports.OpenPanelState = OpenPanelState;
